import { ForwardedRef, forwardRef, useImperativeHandle, useRef } from 'react';

import { CheckCircleFilled, CloseCircleFilled, WarningFilled } from '@ant-design/icons';
import { BankCodesForAll } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { semanticColors, thematicColors } from '@revenuemarket/design';
import { ICellRendererComp, ICellRendererParams } from 'ag-grid-community';
import { Typography } from 'antd';

import { WireListItem } from '~components/BankWiring/types';

interface Props extends ICellRendererParams<WireListItem> {
  onRetry: (data: {
    selectedRowIndex: number;
    organizationCode: BankCodesForAll | null;
    accountNumber: string;
    businessRegNo?: string;
  }) => void;
}

export type AccountHolderStatus = 'CHECKED' | 'FAILED' | 'WARNING' | 'RETRY' | 'PENDING';

export const getStatusRenderer = ({
  status,
  holder,
  onRetry,
}: {
  status: AccountHolderStatus;
  onRetry: () => void;
  holder?: string;
}) => {
  switch (status) {
    case 'CHECKED':
      return (
        <Flex align="center" gap={16}>
          <CheckCircleFilled
            width={16}
            style={{
              color: semanticColors.success.medium,
            }}
          />
          <Typography.Text>{holder}</Typography.Text>
        </Flex>
      );
    case 'FAILED':
      return (
        <Flex align="center" gap={16}>
          <CloseCircleFilled
            width={16}
            style={{
              color: semanticColors.danger.medium,
            }}
          />
          <Typography.Text
            style={{
              color: semanticColors.danger.medium,
            }}
          >
            예금주를 찾을 수 없습니다
          </Typography.Text>
        </Flex>
      );
    case 'WARNING':
      return (
        <Flex align="center" gap={16}>
          <WarningFilled
            width={16}
            style={{
              color: '#FAAD14',
            }}
          />
          <Typography.Text>{holder}</Typography.Text>
        </Flex>
      );
    case 'RETRY':
      return (
        <Typography.Text
          style={{
            textDecoration: 'underline',
            cursor: 'pointer',
            color: thematicColors.primary.medium,
          }}
          onClick={() => onRetry()}
        >
          클릭하여 다시 시도
        </Typography.Text>
      );
    case 'PENDING':
      return <Typography.Text disabled>다음 단계에서 확인</Typography.Text>;
  }
};

const AGGridAccountCheckHolderRenderer = (
  { data, onRetry, node }: Props,
  ref: ForwardedRef<ICellRendererComp>,
) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      getGui: () => containerRef.current ?? document.body,
      refresh: () => true,
    }),
    [],
  );

  if (!data) {
    return null;
  }

  return (
    <Flex ref={containerRef} align="center" style={{ height: '100%', cursor: 'pointer' }}>
      {getStatusRenderer({
        status: data?.accountCheckStatus as AccountHolderStatus,
        holder: data?.accountCheckedHolder,
        onRetry: () =>
          onRetry({
            selectedRowIndex: node.rowIndex ?? -1,
            organizationCode: data?.organizationCode,
            accountNumber: data?.accountNumber,
            businessRegNo: data?.businessRegNo,
          }),
      })}
    </Flex>
  );
};

export default forwardRef(AGGridAccountCheckHolderRenderer);
